import { DragDropModule } from '@angular/cdk/drag-drop';
import { CommonModule, DatePipe, DecimalPipe, PercentPipe } from '@angular/common';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { CKEditorModule } from '@ckeditor/ckeditor5-angular';
import { IotaIcon, IotaUiComponentsModule, MatTooltipModule } from '@iota/iota-ui-components';
import { NgApexchartsModule } from 'ng-apexcharts';
import { AddressListComponent } from './components/address-list/address-list.component';
import { AddressComponent } from './components/address/address.component';
import { AttributeListComponent } from './components/attribute-list/attribute-list.component';
import { AttributeComponent } from './components/attribute/attribute.component';
import { AttributesHelpComponent } from './components/attributes-help/attributes-help.component';
import { BankDetailsComponent } from './components/bank-details/bank-details.component';
import { CompanyAvatarComponent } from './components/company-avatar/company-avatar.component';
import { CompanyCardComponent } from './components/company-card/company-card.component';
import { CompanyFilterComponent } from './components/company-filter/company-filter.component';
import { DocumentListComponent } from './components/document-list/document-list.component';
import { LabelSelectionDialogComponent } from './components/document-list/label-selection-dialog/label-selection-dialog.component';
import { EmailComponent } from './components/email/email.component';
import { EmployeeAvatarComponent } from './components/employee-avatar/employee-avatar.component';
import { EmployeeCardComponent } from './components/employee-card/employee-card.component';
import { FileUploaderComponent } from './components/file-uploader/file-uploader.component';
import { FilterComponent } from './components/filter/filter.component';
import { HealthInsurerCardComponent } from './components/health-insurer-card/health-insurer-card.component';
import { HrUserCardComponent } from './components/hr-user-card/hr-user-card.component';
import { NoteListComponent } from './components/note-list/note-list.component';
import { OfficeAvatarComponent } from './components/office-avatar/office-avatar.component';
import { OfficeCardComponent } from './components/office-card/office-card.component';
import { PersonCardComponent } from './components/person-card/person-card.component';
import { PhoneNumberComponent } from './components/phone-number/phone-number.component';
import { ProductAgreementComponent } from './components/product-agreement/product-agreement.component';
import { ProductPricingOptionListComponent } from './components/product-pricing-option-list/product-pricing-option-list.component';
import { ProductPricingOptionComponent } from './components/product-pricing-option-list/product-pricing-option/product-pricing-option.component';
import { RestrictionComponent } from './components/restriction/restriction.component';
import { SectionHeaderComponent } from './components/section-header/section-header.component';
import { SkeletonComponent } from './components/skeleton/skeleton.component';
import { SubSectionHeaderComponent } from './components/sub-section-header/sub-section-header.component';
import { DialogConfirmationComponent } from './dialogs/dialog-confirmation/dialog-confirmation.component';
import { DialogCreateAgreementComponent } from './dialogs/dialog-create-agreement/dialog-create-agreement.component';
import { DialogImportAttributesComponent } from './dialogs/dialog-import-attributes/dialog-import-attributes.component';
import { DialogPersonComponent } from './dialogs/dialog-person/dialog-person.component';
import { DialogSendMailComponent } from './dialogs/dialog-send-mail/dialog-send-mail.component';
import { DialogShowDocumentComponent } from './dialogs/dialog-show-document/dialog-show-document.component';
import { DialogUploadDocumentComponent } from './dialogs/dialog-upload-document/dialog-upload-document.component';
import { LogoDialogComponent } from './dialogs/logo-dialog/logo-dialog.component';
import { DialogValidationFailedComponent } from './dialogs/dialog-validation-failed/dialog-validation-failed.component';
import { EmployeeDocumentResubmissionDialogComponent } from './dialogs/employee-document-resubmission-dialog/employee-document-resubmission-dialog.component';
import { DndDocumentDirective } from './directives/dnd-document.directive';
import { ResizeDirective } from './directives/resize.directive';
import { Hyphenate } from './pipes/hyphenate.pipe';
import { TimeAgo } from './pipes/time-ago.pipe';
import { Truncate } from './pipes/truncate.pipe';
import { DialogConfigureSelectionDescriptionsComponent } from './dialogs/dialog-configure-selection-descriptions/dialog-configure-selection-descriptions.component';
import { SelectionDescriptionComponent } from './dialogs/dialog-configure-selection-descriptions/selection-description/selection-description.component';
import { RegistrationPeriodProductOverviewComponent } from './components/registration-period-product-overview/registration-period-product-overview.component';
import { RegistrationPeriodProductCardComponent } from './components/registration-period-product-overview/registration-period-product-card/registration-period-product-card.component';
import { DialogSelectRegistrationProductsComponent } from './dialogs/dialog-select-registration-products/dialog-select-registration-products.component';
import { DialogConfigureRegistrationProductComponent } from './dialogs/dialog-configure-registration-product/dialog-configure-registration-product.component';
import { AttributeListV2Component } from './components/attribute-list-v2/attribute-list-v2.component';
import { SimplifiedAttributeComponent } from './components/attribute-list-v2/simplified-attribute/simplified-attribute.component';
import { AttributeV2Component } from './components/attribute-list-v2/attribute-v2/attribute-v2.component';
import { RequestAdditionalInformationDropdownComponent } from './components/request-additional-information-dropdown/request-additional-information-dropdown.component';
import { AttributeDetailsDirective } from './components/attribute-list-v2/attribute-details.directive';
import { AttributeValueDirective } from './components/attribute-list-v2/attribute-value.directive';
import { DynamicHooksModule, HookParserEntry } from 'ngx-dynamic-hooks';
import { SmartTableComponent } from './components/smart-table/smart-table.component';
import { CategoryFilterTabsComponent } from './components/category-filter-tabs/category-filter-tabs.component';
import { DocumentUploadComponent } from './components/document-upload/document-upload.component';
import { SmartAssistantTileComponent } from './components/smart-assistant-tile/smart-assistant-tile.component';
import { SmartDocumentUploadDialogComponent } from './dialogs/smart-document-upload-dialog/smart-document-upload-dialog.component';
import { SmartDialogStepsComponent } from './components/smart-dialog-steps/smart-dialog-steps.component';
import { SmartDialogResultComponent } from './components/smart-dialog-result/smart-dialog-result.component';
import { SmartDocumentUploadFileUploadComponent } from './dialogs/smart-document-upload-dialog/smart-document-upload-file-upload/smart-document-upload-file-upload.component';
import { SubpageSidebarComponent } from './components/subpage-sidebar/subpage-sidebar.component';
import { IotaTabsComponent } from './components/iota-tabs/iota-tabs.component';
import { IotaTabComponent } from './components/iota-tabs/iota-tab/iota-tab.component';
import { IotaTabContentComponent } from './components/iota-tabs/iota-tab-content/iota-tab-content.component';
import { DndDirective } from './directives/dnd.directive';
import { OfficeDetailsDialogComponent } from './dialogs/office-details-dialog/office-details-dialog.component';
import { DialogCreateOfficeComponent } from './dialogs/dialog-create-office/dialog-create-office.component';
import { CdkScrollable } from '@angular/cdk/overlay';
import { TranslationDialogComponent } from './components/translation-dialog/translation-dialog.component';
import { SafeHtmlBypassPipe } from './pipes/safe-html-bypass.pipe';
import { PushPipe } from '@ngrx/component';
import { PlatformLicenseDialogComponent } from './dialogs/platform-license-dialog/platform-license-dialog.component';
import { PlatformLicenseTableComponent } from './components/platform-license-table/platform-license-table.component';
import { PlatformLicenseFormComponent } from './components/platform-license-form/platform-license-form.component';
import { DotsLoadingAnimationComponent } from './components/dots-loading-animation/dots-loading-animation.component';
import { PlatformLicenseActivationFormComponent } from './components/platform-license-activation-form/platform-license-activation-form.component';
import { PlatformLicenseActivationDialogComponent } from './dialogs/platform-license-activation-dialog/platform-license-activation-dialog.component';
import { StartInvoiceProcessDialogComponent } from './dialogs/start-invoice-process-dialog/start-invoice-process-dialog.component';
import { InvoiceReviewDialogComponent } from './dialogs/invoice-review-dialog/invoice-review-dialog.component';
import { FeedbackBannerComponent } from './components/feedback-banner/feedback-banner.component';
import { CreatePlatformLicensePricingDialogComponent } from './dialogs/create-platform-license-pricing-dialog/create-platform-license-pricing-dialog.component';
import { CreatePlatformLicenseDialogComponent } from './dialogs/create-platform-license-dialog/create-platform-license-dialog.component';
import { PlatformLicensePricingComponent } from './components/platform-license-pricing/platform-license-pricing.component';
import { PlatformLicensePricingFormComponent } from './components/platform-license-pricing-form/platform-license-pricing-form.component';
import { ProductPriceScaleFormComponent } from './components/product-price-scale-form/product-price-scale-form.component';
import { EmployeeGroupChangeDialogComponent } from './dialogs/employee-group-change-dialog/employee-group-change-dialog.component';
import { EmployeeGroupChangeDialogValidationDataComponent } from './dialogs/employee-group-change-dialog/employee-group-change-dialog-validation-data/employee-group-change-dialog-validation-data.component';
import { MonthAndYearPickerComponent } from './components/month-and-year-picker/month-and-year-picker.component';
import { ReportReviewDialogComponent } from './dialogs/report-review-dialog/report-review-dialog.component';
import { StartPayrollReportProcessDialogComponent } from './dialogs/start-payroll-report-process-dialog/start-payroll-report-process-dialog.component';
import { EmployeeLeavingDialogComponent } from './dialogs/employee-leaving-dialog/employee-leaving-dialog.component';
import { EmployeeLeavingDialogValidationDataComponent } from './dialogs/employee-leaving-dialog/employee-leaving-dialog-validation-data/employee-leaving-dialog-validation-data.component';
import { SmartAssistantDialogComponent } from './dialogs/smart-assistant-dialog/smart-assistant-dialog.component';
import { SmartAssistantUploadStepComponent } from './dialogs/smart-assistant-dialog/steps/smart-assistant-upload-step.component';
import { SmartAssistantColumnMappingStepComponent } from './dialogs/smart-assistant-dialog/steps/smart-assistant-column-mapping-step.component';
import { SmartAssistantValidationStepComponent } from './dialogs/smart-assistant-dialog/steps/smart-assistant-validation-step.component';
import { SmartAssistantValidationTableComponent } from './dialogs/smart-assistant-dialog/validation-tables/smart-assistant-validation-table.component';
import { SmartAssistantProductStepComponent } from './dialogs/smart-assistant-dialog/steps/smart-assistant-product-step.component';
import { SmartAssistantUrbanSportsClubDateStepComponent } from './dialogs/smart-assistant-dialog/steps/smart-assistant-urban-sports-club-date-step.component';
import { SmartAssistantCompletionStepComponent } from './dialogs/smart-assistant-dialog/steps/smart-assistant-completion-step.component';

const componentParsers: Array<HookParserEntry> = [{ component: IotaIcon }];

@NgModule({
    declarations: [
        SectionHeaderComponent,
        FilterComponent,
        CompanyCardComponent,
        SubSectionHeaderComponent,
        AttributeComponent,
        OfficeCardComponent,
        AddressComponent,
        DialogConfirmationComponent,
        DialogValidationFailedComponent,
        NoteListComponent,
        EmployeeCardComponent,
        PersonCardComponent,
        DialogPersonComponent,
        BankDetailsComponent,
        AttributeListComponent,
        ProductAgreementComponent,
        AttributesHelpComponent,
        DialogCreateAgreementComponent,
        HrUserCardComponent,
        DocumentListComponent,
        ProductPricingOptionListComponent,
        ProductPricingOptionComponent,
        HealthInsurerCardComponent,
        ResizeDirective,
        RestrictionComponent,
        EmployeeAvatarComponent,
        Hyphenate,
        Truncate,
        TimeAgo,
        DndDocumentDirective,
        EmailComponent,
        PhoneNumberComponent,
        LabelSelectionDialogComponent,
        DialogShowDocumentComponent,
        DialogSendMailComponent,
        DialogUploadDocumentComponent,
        EmployeeDocumentResubmissionDialogComponent,
        CompanyAvatarComponent,
        OfficeAvatarComponent,
        DialogImportAttributesComponent,
        FileUploaderComponent,
        CompanyFilterComponent,
        AddressListComponent,
        DialogConfigureSelectionDescriptionsComponent,
        SelectionDescriptionComponent,
        RegistrationPeriodProductOverviewComponent,
        RegistrationPeriodProductCardComponent,
        DialogSelectRegistrationProductsComponent,
        DialogConfigureRegistrationProductComponent,
        AttributeListV2Component,
        SimplifiedAttributeComponent,
        AttributeV2Component,
        RequestAdditionalInformationDropdownComponent,
        AttributeDetailsDirective,
        AttributeValueDirective,
        SmartTableComponent,
        CategoryFilterTabsComponent,
        DocumentUploadComponent,
        SmartAssistantTileComponent,
        SmartDocumentUploadDialogComponent,
        SmartDialogStepsComponent,
        SmartDialogResultComponent,
        SmartDocumentUploadFileUploadComponent,
        SubpageSidebarComponent,
        IotaTabsComponent,
        IotaTabComponent,
        IotaTabContentComponent,
        DndDirective,
        OfficeDetailsDialogComponent,
        DialogCreateOfficeComponent,
        TranslationDialogComponent,
        SafeHtmlBypassPipe,
        PlatformLicenseDialogComponent,
        PlatformLicenseTableComponent,
        PlatformLicenseFormComponent,
        StartInvoiceProcessDialogComponent,
        InvoiceReviewDialogComponent,
        PlatformLicenseActivationFormComponent,
        PlatformLicenseActivationDialogComponent,
        FeedbackBannerComponent,
        CreatePlatformLicensePricingDialogComponent,
        CreatePlatformLicenseDialogComponent,
        PlatformLicensePricingComponent,
        PlatformLicensePricingFormComponent,
        ProductPriceScaleFormComponent,
        EmployeeGroupChangeDialogComponent,
        EmployeeGroupChangeDialogValidationDataComponent,
        MonthAndYearPickerComponent,
        ReportReviewDialogComponent,
        StartPayrollReportProcessDialogComponent,
        EmployeeLeavingDialogComponent,
        EmployeeLeavingDialogValidationDataComponent,
        SmartAssistantDialogComponent,
        SmartAssistantUploadStepComponent,
        SmartAssistantColumnMappingStepComponent,
        SmartAssistantValidationStepComponent,
        SmartAssistantValidationTableComponent,
        SmartAssistantProductStepComponent,
        SmartAssistantUrbanSportsClubDateStepComponent,
        SmartAssistantCompletionStepComponent,
        LogoDialogComponent
    ],
    imports: [
        CommonModule,
        IotaUiComponentsModule,
        RouterModule,
        ReactiveFormsModule,
        FormsModule,
        NgApexchartsModule,
        DragDropModule,
        CKEditorModule,
        MatTooltipModule,
        PushPipe,
        DynamicHooksModule.forRoot({
            globalParsers: componentParsers,
            globalOptions: {
                sanitize: false
            }
        }),
        DotsLoadingAnimationComponent,
        SkeletonComponent
    ],
    exports: [
        RouterModule,
        IotaUiComponentsModule,
        ReactiveFormsModule,
        SectionHeaderComponent,
        SubSectionHeaderComponent,
        FilterComponent,
        FormsModule,
        CompanyCardComponent,
        OfficeCardComponent,
        SkeletonComponent,
        AttributeComponent,
        AddressComponent,
        DialogConfirmationComponent,
        NoteListComponent,
        EmployeeCardComponent,
        PersonCardComponent,
        DialogPersonComponent,
        BankDetailsComponent,
        NgApexchartsModule,
        AttributeListComponent,
        AttributesHelpComponent,
        ProductPricingOptionListComponent,
        ProductPricingOptionComponent,
        HealthInsurerCardComponent,
        ResizeDirective,
        RestrictionComponent,
        ProductAgreementComponent,
        EmployeeAvatarComponent,
        Hyphenate,
        Truncate,
        TimeAgo,
        DndDirective,
        DndDocumentDirective,
        DragDropModule,
        EmailComponent,
        PhoneNumberComponent,
        DocumentListComponent,
        DialogShowDocumentComponent,
        CKEditorModule,
        HrUserCardComponent,
        EmployeeDocumentResubmissionDialogComponent,
        CompanyAvatarComponent,
        PushPipe,
        FileUploaderComponent,
        DialogImportAttributesComponent,
        CompanyFilterComponent,
        AddressListComponent,
        RegistrationPeriodProductOverviewComponent,
        AttributeListV2Component,
        RequestAdditionalInformationDropdownComponent,
        DynamicHooksModule,
        SmartTableComponent,
        CategoryFilterTabsComponent,
        SmartAssistantTileComponent,
        SubpageSidebarComponent,
        IotaTabsComponent,
        IotaTabComponent,
        IotaTabContentComponent,
        CdkScrollable,
        TranslationDialogComponent,
        SafeHtmlBypassPipe,
        PlatformLicenseTableComponent,
        PlatformLicenseFormComponent,
        InvoiceReviewDialogComponent,
        PlatformLicenseActivationFormComponent,
        PlatformLicenseActivationDialogComponent,
        PlatformLicensePricingComponent,
        PlatformLicensePricingFormComponent,
        ProductPriceScaleFormComponent,
        CreatePlatformLicensePricingDialogComponent,
        CreatePlatformLicenseDialogComponent,
        FeedbackBannerComponent,
        EmployeeGroupChangeDialogComponent,
        MonthAndYearPickerComponent,
        SmartDialogStepsComponent
    ],
    providers: [PercentPipe, DecimalPipe, DatePipe]
})
export class SharedModule {}
